import { useEffect } from "react";
import { Button } from "@/common/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message: string;
  itemName: string;
  isLoading?: boolean;
  confirmText?: string;
  cancelText?: string;
}

const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Delete",
  message,
  itemName,
  isLoading = false,
  confirmText = "Yes, Delete",
  cancelText = "No",
}: DeleteConfirmationModalProps) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] m-0 h-screen w-screen p-0">
      {/* Full Screen Blurred Backdrop */}
      <div
        className="fixed inset-0 m-0 h-screen w-screen bg-black/30 p-0 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Centered Modal */}
      <div className="fixed inset-0 m-0 flex h-screen w-screen items-center justify-center p-0 p-4">
        <div
          className="relative z-[10000] w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-start gap-4">
            {/* Warning Icon */}
            <div className="flex-shrink-0">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>

            {/* Message and Buttons */}
            <div className="flex-1">
              <h3 className="mb-2 text-lg font-bold text-slate-900">{title}</h3>
              <p className="mb-6 text-sm text-slate-600">
                {message}{" "}
                <span className="font-semibold text-slate-900">{itemName}</span>
                ? This action cannot be undone.
              </p>

              {/* Buttons */}
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={isLoading}
                  className="hover:bg-slate-60 h-10 border-slate-200 px-6"
                >
                  {cancelText}
                </Button>
                <Button
                  type="button"
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="h-10 bg-red-600 px-6 text-white hover:bg-red-700"
                >
                  {isLoading ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    confirmText
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
